import { Component, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { HelpDeskComponent } from "./components/help-desk/help-desk.component";
import { TicketDetailsComponent } from "./components/ticket-details/ticket-details.component";
import { UserService } from './services/user.service';
import * as CryptoJS from 'crypto-js';

@Component({
  selector: 'app-root',
  imports: [HelpDeskComponent, TicketDetailsComponent, NgIf],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  path: string = "Help Desk";
  title = 'angular-helpdesk';
  ticketDetails!: any;
  isSessionOngoing: boolean = false;
  showLoader: boolean = true;
  quickComplete: boolean = false;
  initialAnimationComplete: boolean = false;

  private updateSessionOngoing(): void {
    const wasSessionOngoing = this.isSessionOngoing;
    this.isSessionOngoing = Boolean(
      sessionStorage.getItem('auth_token') &&
      sessionStorage.getItem('userName') &&
      sessionStorage.getItem('userID') && 
      sessionStorage.getItem('emailAddress')
    );
    console.log('[AppComponent] Session ongoing updated:', this.isSessionOngoing);
    
    // If session just became active, trigger completion
    if (!wasSessionOngoing && this.isSessionOngoing && this.showLoader) {
      this.handleSessionActive();
    }
  }



  private handleSessionActive(): void {
    if (this.initialAnimationComplete) {
      // If 60% animation is done, immediately start quick completion
      this.quickComplete = true;
      setTimeout(() => {
        this.showLoader = false;
      }, 500);
    }
    // If 60% animation is not done yet, the timeout below will handle it
  }



  private startInitialAnimation(): void {
    // Mark when the initial 60% animation completes
    setTimeout(() => {
      this.initialAnimationComplete = true;
      // If session is already active, start quick completion
      if (this.isSessionOngoing) {
        this.quickComplete = true;
        setTimeout(() => {
          this.showLoader = false;
        }, 500);
      }
    }, 3000); // 3 seconds for initial animation to 60%
  }

  encryptEmail(): void {
    let iv = CryptoJS.enc.Utf8.parse('1234567890123456');
    let key = CryptoJS.enc.Utf8.parse('1234567890123456');

    const email = window.sessionStorage.getItem('emailAddress');
    if (email) {
      window.sessionStorage.setItem('ecryptedEMail', CryptoJS.AES.encrypt(email, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }).toString());
    } else {
      console.warn('No emailAddress found in sessionStorage to encrypt.');
    }
  }

  navigate(path: string) {
    this.path = path
  }

  openTicket(ticketDetails: any) {
    this.path = "Ticket Details"
    this.ticketDetails = ticketDetails
  }

  constructor(private readonly user: UserService) { }

  ngOnInit(): void {

    const hasOpener = !!window.opener;
    const isFramed = window.self !== window.top;

    if (!hasOpener && !isFramed) {
      // Site B opened directly → no message will ever come
      console.warn('[AppComponent] Opened directly. Redirecting to login.');

      const currentURL = window.location.hostname;

      if (currentURL === 'helpdesk.teamleaseregtech.com') {
        window.location.href = 'https://login.teamleaseregtech.com/';
        return;
      } else if (currentURL === 'helpdesk.avantisregtec.in') {
        window.location.href = 'https://uat.avantisregtec.in/';
        return;
      } else if (currentURL === 'demohelpdesk.teamleaseregtech.com') {
        window.location.href = 'https://demo.teamleaseregtech.com/';
        return;
      } else if (currentURL === 'regtrackapp.avantisregtec.in') {
        window.location.href = 'https://uat.avantisregtec.in/';
        return;
      }

      return;
    }

    console.log('AppComponent initialized');

    const domainMap: { [key: string]: string } = {
      'https://login.teamleaseregtech.com': 'L',
      'https://demo.teamleaseregtech.com': 'D',
      'https://uat.avantisregtec.in': 'U',
      'https://impl.teamleaseregtech.com': 'U',
      'https://regtrackapp.avantisregtec.in': 'U',
      'https://compliance.teamleaseregtech.com': 'L',
    };

    const allowedOrigins = [
      'https://uat.avantisregtec.in',
      'https://login.teamleaseregtech.com',
      'https://demo.teamleaseregtech.com',
      'https://impl.teamleaseregtech.com',
      'https://regtrackapp.avantisregtec.in',
      'https://compliance.teamleaseregtech.com'
    ];

    this.updateSessionOngoing();
    this.startInitialAnimation(); // Start the initial animation timer

    if (!this.isSessionOngoing) {

      const messageHandler = (event: MessageEvent) => {

        const origin = event.origin;
        const domainCode = domainMap[origin];

        console.log('Detected origin:', event.origin);

        console.log('Mapped domainCode:', domainCode);

        if (domainCode) {
          sessionStorage.setItem("Domain", domainCode);
          console.log(`[AppComponent] Set sessionStorage Domain to: ${domainCode}`);
        } else {
          console.warn('Unknown domain:', origin);
          sessionStorage.setItem("Domain", 'LO');
          console.log('[AppComponent] Set sessionStorage Domain to fallback: LO');
        }


        if (!allowedOrigins.includes(event.origin)) {
          console.warn('[AppComponent] Blocked message from untrusted origin:', event.origin);
          return;
        }
        if (!event.data || !event.data.email_id) {
          console.warn('[AppComponent] Message missing email_id:', event.data);
          return;
        }
        console.log('[AppComponent] Accepted message from allowed origin:', event.origin);

        window.removeEventListener('message', messageHandler);

        this.user.authenticateAndStoreUserData(event.data.email_id).then(() => {
          this.user.validateUser().subscribe({
            next: (response) => {
              this.encryptEmail();
              sessionStorage.setItem('auth_token', response.access_token);
              // Use updateSessionOngoing to properly handle session state change
              this.updateSessionOngoing();
            },
            error: (error) => {
              console.error('[AppComponent] Validation failed:', error);
            }
          });
        }).catch((error) => {
          console.error('[AppComponent] Authentication or user data fetch failed:', error)
        });
      };

      window.addEventListener('message', messageHandler);

    }
  }
}
